import React, { FC, SyntheticEvent, useState, useMemo } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  useMediaQuery,
  useTheme,
  Button,
  Stack,
  TablePagination,
} from '@mui/material';
import { TableRows, Download } from '@mui/icons-material';
import {
  DeviceReadingsResponseDto,
  DeviceReadingResponseDto,
  DisplayUnit,
  MeasurementUnit,
} from '@thingslog/repositories';
import ReadingsExportDto from '@thingslog/repositories/src/readingsExport/ReadingsExportDto';
import { SensorDataView } from '../../SensorsData.page';
import { TableLoadingSkeleton } from '../skeletons/TableLoadingPlaceholder';
import { InfoDisplay } from '../InfoDisplay';
import {
  GnssReadingResponseDto,
  TimeSeriesDataItemDto,
} from '@thingslog/repositories/src/deviceCounter/DeviceReadingsResponseDto';
import { useTranslation } from 'react-i18next';

interface TabPanelProps {
  children?: React.ReactNode;
  show: boolean;
  id: string;
}

function TabPanel(props: TabPanelProps): JSX.Element {
  const { children, show, id, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={!show}
      id={`readings-tabpanel-${id}`}
      aria-labelledby={`readings-tab-${id}`}
      className="h-full"
      {...other}
    >
      {show && (
        <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface ReadingsDataDisplayProps {
  isMobile: boolean;
  selectedTab: 'raw' | 'export';
  onSelectedTabChange: (tab: 'raw' | 'export') => void;
  rawData: DeviceReadingsResponseDto | undefined;
  exportData: ReadingsExportDto[] | undefined;
  rawPage: number;
  onRawPageChange: (page: number) => void;
  rawRowsPerPage: number;
  onRawRowsPerPageChange: (rowsPerPage: number) => void;
  exportPage: number;
  onExportPageChange: (page: number) => void;
  exportRowsPerPage: number;
  onExportRowsPerPageChange: (rowsPerPage: number) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  exportFormat: 'CSV' | 'EXCEL';
  onExportFormatChange: (format: 'CSV' | 'EXCEL') => void;
  onExportClicked: (format: 'CSV' | 'EXCEL') => void;
  isLoading?: boolean;
}

interface RawTableRow {
  deviceNumber: string;
  deviceName: string;
  sensorName: string;
  sensorUnit: MeasurementUnit;
  date: string;
  value: string;
}

export const ReadingsDataDisplay: FC<ReadingsDataDisplayProps> = ({
  isMobile,
  selectedTab,
  onSelectedTabChange,
  rawData,
  exportData,
  rawPage,
  onRawPageChange,
  rawRowsPerPage,
  onRawRowsPerPageChange,
  exportPage,
  onExportPageChange,
  exportRowsPerPage,
  onExportRowsPerPageChange,
  isExpanded,
  onToggleExpanded,

  isLoading = false,
  exportFormat,
  onExportFormatChange,
  onExportClicked,
}: ReadingsDataDisplayProps) => {
  const { t } = useTranslation();
  const handleTabChange = (event: SyntheticEvent, newValue: string): void => {
    onSelectedTabChange(newValue as 'raw' | 'export');
  };

  const handleExportFormatChange = (event: { target: { value: unknown } }): void => {
    onExportFormatChange(event.target.value as 'CSV' | 'EXCEL');
  };

  const handleDownload = (): void => {
    onExportClicked(exportFormat);
  };

  // Raw data pagination handlers
  const handleChangeRawPage = (event: unknown, newPage: number): void => {
    onRawPageChange(newPage);
  };

  const handleChangeRawRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onRawRowsPerPageChange(parseInt(event.target.value, 10));
    onRawPageChange(0);
  };

  // Export data pagination handlers
  const handleChangeExportPage = (event: unknown, newPage: number): void => {
    onExportPageChange(newPage);
  };

  const handleChangeExportRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onExportRowsPerPageChange(parseInt(event.target.value, 10));
    onExportPageChange(0);
  };

  const rawTableData = useMemo(() => {
    if (!rawData?.devicesReadings) {
      return [];
    }

    const rows: RawTableRow[] = [];
    rawData.devicesReadings.forEach((deviceReading: DeviceReadingResponseDto) => {
      const isNumeric = deviceReading.sensorData === 'NUMERIC';
      // if (deviceReading.sensorData === 'NUMERIC') {
      deviceReading.readings.forEach((reading: TimeSeriesDataItemDto | GnssReadingResponseDto) => {
        let date = '';
        let value = '';
        if (isNumeric) {
          const numericReading = reading as TimeSeriesDataItemDto;
          date = numericReading.date;
          value = numericReading.value.toString();
        } else {
          const gnssReading = reading as GnssReadingResponseDto;
          date = gnssReading.timestamp;
          value = `lat:${gnssReading.latitude}, lon:${gnssReading.longitude}`;
        }
        rows.push({
          deviceNumber: deviceReading.deviceNumber,
          deviceName: deviceReading.deviceName,
          sensorName: deviceReading.sensorName,
          sensorUnit: deviceReading.sensorUnit,
          date: date,
          value: value,
        });
      });
      // }
    });
    return rows;
  }, [rawData]);

  if (isLoading) {
    return (
      <div className="h-[500px] ">
        <TableLoadingSkeleton expanded={isExpanded} />
      </div>
    );
  }

  if (!isExpanded) {
    return (
      <InfoDisplay
        icon={<TableRows />}
        title={t('sensors_data_click_to_view_readings')}
        subtitle={t('sensors_data_view_sensor_data_subtitle')}
        onClick={onToggleExpanded}
        highlightOnHover
      />
    );
  }

  return (
    <Paper className="flex flex-col h-full w-full pt-2" elevation={2}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Tabs value={selectedTab} onChange={handleTabChange} aria-label="readings data tabs">
          <Tab label={t('sensors_data_raw')} value="raw" />
          <Tab label={t('sensors_data_export')} value="export" />
        </Tabs>
        {selectedTab === 'export' && !isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="export-format-label">{t('sensors_data_format')}</InputLabel>
              <Select
                labelId="export-format-label"
                value={exportFormat}
                onChange={handleExportFormatChange}
                label={t('sensors_data_format')}
              >
                <MenuItem value="CSV">{t('sensors_data_csv')}</MenuItem>
                <MenuItem value="EXCEL">{t('sensors_data_excel')}</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={handleDownload} color="primary" size="small">
              <Download />
            </IconButton>
          </Box>
        )}
      </Box>
      <div className="flex-1 overflow-hidden">
        <TabPanel show={selectedTab === 'raw'} id="raw">
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t('sensors_data_device_number')}</TableCell>
                  <TableCell>{t('sensors_data_device_name')}</TableCell>
                  <TableCell>{t('sensors_data_sensor_name')}</TableCell>
                  <TableCell>{t('sensors_data_date')}</TableCell>
                  <TableCell align="right">{t('sensors_data_value')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rawTableData.length > 0 ? (
                  rawTableData
                    .slice(rawPage * rawRowsPerPage, rawPage * rawRowsPerPage + rawRowsPerPage)
                    .map((row: RawTableRow, index: number) => (
                      <TableRow
                        key={`${row.deviceNumber}-${row.sensorName}-${row.date}-${index}`}
                        hover
                      >
                        <TableCell>{row.deviceNumber}</TableCell>
                        <TableCell>{row.deviceName}</TableCell>
                        <TableCell>{row.sensorName}</TableCell>
                        <TableCell>{new Date(row.date).toLocaleString()}</TableCell>
                        <TableCell align="right">
                          {row.value} {DisplayUnit[row.sensorUnit]}
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="subtitle1" color="gray" sx={{ py: 2 }}>
                        {t('sensors_data_no_data_available')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25]}
            component="div"
            count={rawTableData.length}
            rowsPerPage={rawRowsPerPage}
            page={rawPage}
            onPageChange={handleChangeRawPage}
            onRowsPerPageChange={handleChangeRawRowsPerPage}
          />
        </TabPanel>
        <TabPanel show={selectedTab === 'export'} id="export">
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t('sensors_data_device_number')}</TableCell>
                  <TableCell>{t('sensors_data_sensor_index')}</TableCell>
                  <TableCell>{t('sensors_data_sensor_name')}</TableCell>
                  <TableCell>{t('sensors_data_date')}</TableCell>
                  <TableCell align="right">{t('sensors_data_counters')}</TableCell>
                  <TableCell align="right">{t('sensors_data_previous')}</TableCell>
                  <TableCell align="right">{t('sensors_data_delta')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exportData && exportData.length > 0 ? (
                  exportData
                    .slice(
                      exportPage * exportRowsPerPage,
                      exportPage * exportRowsPerPage + exportRowsPerPage
                    )
                    .map((row: ReadingsExportDto, index: number) => (
                      <TableRow
                        key={`${row.deviceNumber}-${row.sensorIndex}-${row.date}-${index}`}
                        hover
                      >
                        <TableCell>{row.deviceNumber}</TableCell>
                        <TableCell>{row.sensorIndex}</TableCell>
                        <TableCell>{row.sensorName || '-'}</TableCell>
                        <TableCell>{new Date(row.date).toLocaleString()}</TableCell>
                        <TableCell align="right">
                          {row.counters} {DisplayUnit[row.unit ?? MeasurementUnit.NO_UNITS]}
                        </TableCell>
                        <TableCell align="right">
                          {row.previousCounter ?? '-'}{' '}
                          {DisplayUnit[row.unit ?? MeasurementUnit.NO_UNITS]}
                        </TableCell>
                        <TableCell align="right">{row.delta ?? '-'}</TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="subtitle1" color="gray" sx={{ py: 2 }}>
                        {t('sensors_data_no_data_available')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25]}
            component="div"
            count={exportData ? exportData.length : 0}
            rowsPerPage={exportRowsPerPage}
            page={exportPage}
            onPageChange={handleChangeExportPage}
            onRowsPerPageChange={handleChangeExportRowsPerPage}
          />
        </TabPanel>
      </div>
      {!isMobile && (
        <Box
          sx={{
            p: 1,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            onClick={onToggleExpanded}
            fullWidth
            sx={{
              color: 'text.secondary',
              bgcolor: 'action.hover',
              '&:hover': {
                bgcolor: 'action.selected',
              },
              textTransform: 'none',
            }}
          >
            {t('sensors_data_collapse')}
          </Button>
        </Box>
      )}
    </Paper>
  );
};
